import React, { memo, useRef } from 'react';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  placeholder?: string;
  matchCount?: number;
  totalCount?: number;
}

// Icons
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="7" cy="7" r="5" />
    <path d="M11 11l3 3" strokeLinecap="round" />
  </svg>
);

const ClearIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 3l8 8M11 3l-8 8" strokeLinecap="round" />
  </svg>
);

function SearchInputComponent({
  value,
  onChange,
  onClear,
  placeholder = 'Ara...',
  matchCount,
  totalCount
}: SearchInputProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClear = () => {
    onClear();
    inputRef.current?.focus();
  };

  const showMatchCount = value.trim().length > 0 && matchCount !== undefined && totalCount !== undefined;

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {/* Search Icon */}
      <span
        style={{
          position: 'absolute',
          left: 'var(--space-3)',
          color: 'var(--color-text-tertiary)',
          display: 'flex',
          alignItems: 'center',
          pointerEvents: 'none'
        }}
      >
        <SearchIcon />
      </span>

      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '200px',
          padding: 'var(--space-2) var(--space-3)',
          paddingLeft: 'calc(var(--space-3) + 24px)',
          paddingRight: value ? 'calc(var(--space-3) + 24px)' : 'var(--space-3)',
          fontSize: 'var(--text-sm)',
          fontFamily: 'var(--font-body)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border-default)',
          background: 'var(--color-bg-elevated)',
          color: 'var(--color-text-primary)',
          outline: 'none',
          transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)'
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = 'var(--color-primary-500)';
          e.currentTarget.style.boxShadow = '0 0 0 3px var(--color-primary-100)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = 'var(--color-border-default)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      />

      {/* Clear button */}
      {value && (
        <button
          onClick={handleClear}
          style={{
            position: 'absolute',
            right: 'var(--space-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '20px',
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: 'var(--color-neutral-200)',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            transition: 'all var(--transition-fast)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--color-neutral-300)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--color-neutral-200)';
          }}
          title="Aramayı temizle"
        >
          <ClearIcon />
        </button>
      )}

      {/* Match count */}
      {showMatchCount && (
        <span
          style={{
            marginLeft: 'var(--space-2)',
            fontSize: 'var(--text-xs)',
            color: matchCount === 0 ? 'var(--color-error-600)' : 'var(--color-text-secondary)',
            whiteSpace: 'nowrap'
          }}
        >
          {matchCount} of {totalCount}
        </span>
      )}
    </div>
  );
}

export const SearchInput = memo(SearchInputComponent);
